import * as Sentry from '@sentry/react';
import { AlertTriangle, Bug, Home, RefreshCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/** Detect chunk/module load failures caused by stale assets after deployment. */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message?.toLowerCase() ?? '';
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('failed to fetch dynamically imported module')
  );
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Auto-reload on chunk load failures (stale assets after deployment).
    // Guard with sessionStorage to prevent infinite reload loops.
    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem('chunk-error-reload');
      if (!lastReload || Date.now() - Number.parseInt(lastReload, 10) > 10_000) {
        sessionStorage.setItem('chunk-error-reload', Date.now().toString());
        window.location.reload();
        return;
      }
    }

    // Report to Sentry
    Sentry.withScope((scope) => {
      scope.setTag('component', 'ErrorBoundary');
      scope.setLevel('error');
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error } = this.state;
      const isImportError =
        error?.message?.includes('import') ||
        error?.message?.includes('export') ||
        error?.message?.includes('module');
      const isDevelopment = import.meta.env.DEV;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-destructive" />
                </div>
                <CardTitle className="text-2xl">
                  {isImportError ? 'Module Loading Error' : 'Something went wrong'}
                </CardTitle>
                <CardDescription>
                  {isImportError
                    ? 'There was an issue loading a required module. This usually indicates a code or build problem.'
                    : 'An unexpected error occurred while rendering this page.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Error details for development or import errors */}
                {(isDevelopment || isImportError) && error && (
                  <Alert>
                    <Bug className="h-4 w-4" />
                    <AlertDescription>
                      <div className="font-mono text-sm break-all">
                        <div className="font-semibold mb-1">Error:</div>
                        <div className="text-destructive">{error.message}</div>
                        {error.stack && (
                          <details className="mt-2">
                            <summary className="cursor-pointer font-semibold">Stack Trace</summary>
                            <pre className="mt-1 text-xs whitespace-pre-wrap overflow-x-auto">
                              {error.stack}
                            </pre>
                          </details>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button onClick={this.handleRetry} className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </Button>
                  <Button
                    variant="outline"
                    onClick={this.handleReload}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reload Page
                  </Button>
                  <Button
                    variant="outline"
                    onClick={this.handleGoHome}
                    className="flex items-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    Go Home
                  </Button>
                </div>

                {/* Additional help text */}
                <div className="text-sm text-muted-foreground text-center pt-4 border-t">
                  {isImportError ? (
                    <p>
                      If this error persists, there may be a development or build issue. Try
                      refreshing the page or contact support if the problem continues.
                    </p>
                  ) : (
                    <p>
                      This error has been automatically reported. If the problem persists, please
                      contact support.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };
export default ErrorBoundary;
